import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Table from 'react-bootstrap/Table';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPercent, faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import swal from 'sweetalert';
import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  orderBy,
  query,
  setDoc,
  Timestamp,
  updateDoc
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { app } from '../../firebase';
import defaulProfPic from '../../Images/defprofile.png';
import { logAdminActivity } from '../../utils/activityLogger';

const normalizeSecondaryCourses = (secondaryCourses = []) => {
  if (!Array.isArray(secondaryCourses)) return [];

  return secondaryCourses
    .map((entry) => {
      if (typeof entry === 'string') {
        return {
          name: entry,
          intakeId: '',
          intakeDate: '',
          rawIntakeDate: '',
          campus: '',
          status: 'Pending',
          admissionDate: '',
          addedAt: ''
        };
      }

      return {
        name: entry?.name || entry?.courseName || '',
        intakeId: entry?.intakeId || '',
        intakeDate: entry?.intakeDate || entry?.intake || '',
        rawIntakeDate: entry?.rawIntakeDate || '',
        campus: entry?.campus || '',
        status: entry?.status || 'Pending',
        admissionDate: entry?.admissionDate || '',
        addedAt: entry?.addedAt || entry?.createdAt || ''
      };
    })
    .filter((entry) => entry.name);
};

const toTimestampMs = (value) => {
  if (!value) return 0;
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const formatDateTime = (value) => {
  const timestamp = toTimestampMs(value);
  if (!timestamp) return '—';

  return new Date(timestamp).toLocaleString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatAmount = (value) => {
  const amount = Number(value || 0);
  return `KES ${amount.toLocaleString('en-KE')}`;
};

const normalizeCourseName = (value) => String(value || '').trim().toLowerCase();

const buildSearchBlob = (student = {}) => [
  student.bio,
  student.userId,
  student.course,
  student.userEmail,
  student.phoneNumber,
  student.userStatus,
  student.nationality,
  student.cohort,
  student.appDate
]
  .filter(Boolean)
  .join(' ')
  .toLowerCase();

const getStudentDisplayName = (student = {}) => (
  student.bio || student.userName || student.fullName || student.userEmail || student.userId || 'Unnamed student'
);

function StudentDiscountsComp() {
  const db = getFirestore(app);
  const auth = getAuth(app);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [discounts, setDiscounts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [allStudentOptions, setAllStudentOptions] = useState([]);
  const [loadingStudentOptions, setLoadingStudentOptions] = useState(false);
  const [selectedStudentDocId, setSelectedStudentDocId] = useState('');
  const [selectedStudentRecord, setSelectedStudentRecord] = useState(null);
  const [loadingStudentRecord, setLoadingStudentRecord] = useState(false);
  const [selectedCourseKey, setSelectedCourseKey] = useState('');
  const [discountAmount, setDiscountAmount] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [isSavingDiscount, setIsSavingDiscount] = useState(false);

  const loadDiscounts = useCallback(async () => {
    try {
      const snapshot = await getDocs(query(collection(db, 'StudentDiscounts'), orderBy('createdAt', 'desc')));
      const records = snapshot.docs.map((discountDoc) => ({
        id: discountDoc.id,
        ...discountDoc.data()
      }));
      setDiscounts(records);
    } catch (error) {
      console.error('Error loading student discounts:', error);
      setDiscounts([]);
    } finally {
      setLoading(false);
    }
  }, [db]);

  const loadStudentOptions = useCallback(async () => {
    setLoadingStudentOptions(true);

    try {
      const snapshot = await getDocs(collection(db, 'users'));
      const records = snapshot.docs.map((studentDoc) => {
        const data = studentDoc.data();
        return {
          studentDocId: studentDoc.id,
          userId: data.userId || studentDoc.id,
          bio: data.bio || data.userName || data.fullName || 'Unnamed student',
          profilePic: data.profilePic || defaulProfPic,
          course: data.course || '',
          userEmail: data.userEmail || '',
          phoneNumber: data.phoneNumber || '',
          userStatus: data.userStatus || data.Status || '',
          nationality: data.nationality || '',
          cohort: data.cohort || '',
          appDate: data.appDate || '',
          searchBlob: buildSearchBlob(data)
        };
      });

      setAllStudentOptions(records);
    } catch (error) {
      console.error('Error loading students for discount search:', error);
      setAllStudentOptions([]);
    } finally {
      setLoadingStudentOptions(false);
    }
  }, [db]);

  useEffect(() => {
    loadDiscounts();
    loadStudentOptions();
  }, [loadDiscounts, loadStudentOptions]);

  const uniqueStudentOptions = useMemo(() => {
    const seen = new Set();
    return allStudentOptions.reduce((accumulator, student) => {
      if (!student?.studentDocId || seen.has(student.studentDocId)) {
        return accumulator;
      }

      seen.add(student.studentDocId);
      accumulator.push(student);
      return accumulator;
    }, []);
  }, [allStudentOptions]);

  const filteredStudentOptions = useMemo(() => {
    const normalizedSearch = studentSearchTerm.trim().toLowerCase();
    if (!normalizedSearch) return [];

    return uniqueStudentOptions.filter((student) => (student.searchBlob || '').includes(normalizedSearch));
  }, [studentSearchTerm, uniqueStudentOptions]);

  const selectedStudentCourseOptions = useMemo(() => {
    const options = [];
    const seen = new Set();
    const primaryCourseName = String(selectedStudentRecord?.course || '').trim();

    if (primaryCourseName) {
      const key = `primary:${normalizeCourseName(primaryCourseName)}`;
      seen.add(key);
      options.push({
        key,
        name: primaryCourseName,
        type: 'primary',
        label: `Primary - ${primaryCourseName}`,
        intakeId: '',
        intakeDate: '',
        rawIntakeDate: '',
        campus: ''
      });
    }

    normalizeSecondaryCourses([
      ...(Array.isArray(selectedStudentRecord?.secondaryCourses) ? selectedStudentRecord.secondaryCourses : []),
      selectedStudentRecord?.pendingSecondaryCourse
    ].filter(Boolean)).forEach((entry) => {
      const courseName = String(entry.name || '').trim();
      if (!courseName) return;
      const key = `secondary:${normalizeCourseName(courseName)}:${normalizeCourseName(entry.intakeId)}:${normalizeCourseName(entry.rawIntakeDate || entry.intakeDate)}`;
      if (seen.has(key)) return;
      seen.add(key);

      options.push({
        key,
        name: courseName,
        type: 'secondary',
        label: `Secondary - ${courseName}`,
        intakeId: entry.intakeId || '',
        intakeDate: entry.intakeDate || '',
        rawIntakeDate: entry.rawIntakeDate || '',
        campus: entry.campus || ''
      });
    });

    return options;
  }, [selectedStudentRecord]);

  const selectedCourseOption = useMemo(() => (
    selectedStudentCourseOptions.find((option) => option.key === selectedCourseKey) || null
  ), [selectedCourseKey, selectedStudentCourseOptions]);

  const filteredDiscounts = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return discounts;

    return discounts.filter((discount) => {
      const searchableText = [
        discount.studentName,
        discount.studentEmail,
        discount.phoneNumber,
        discount.courseName,
        discount.studentStatus,
        discount.discountReason,
        discount.createdByEmail,
        discount.discountAmount
      ].join(' ').toLowerCase();

      return searchableText.includes(normalizedSearch);
    });
  }, [discounts, searchTerm]);

  const totalDiscountAmount = useMemo(() => (
    filteredDiscounts.reduce((sum, discount) => sum + Number(discount.discountAmount || 0), 0)
  ), [filteredDiscounts]);

  const uniqueDiscountStudents = useMemo(() => (
    new Set(filteredDiscounts.map((discount) => discount.studentId || discount.studentDocId).filter(Boolean)).size
  ), [filteredDiscounts]);

  const openDiscountModal = useCallback(() => {
    setShowDiscountModal(true);
    setStudentSearchTerm('');
    setSelectedStudentDocId('');
    setSelectedStudentRecord(null);
    setSelectedCourseKey('');
    setDiscountAmount('');
    setDiscountReason('');
  }, []);

  const closeDiscountModal = useCallback(() => {
    setShowDiscountModal(false);
    setStudentSearchTerm('');
    setSelectedStudentDocId('');
    setSelectedStudentRecord(null);
    setLoadingStudentRecord(false);
    setSelectedCourseKey('');
    setDiscountAmount('');
    setDiscountReason('');
  }, []);

  useEffect(() => {
    if (!selectedStudentDocId) {
      setSelectedStudentRecord(null);
      setSelectedCourseKey('');
      return;
    }

    let cancelled = false;

    const loadSelectedStudent = async () => {
      setLoadingStudentRecord(true);
      try {
        const snapshot = await getDoc(doc(db, 'users', selectedStudentDocId));
        if (cancelled) return;

        const record = snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
        setSelectedStudentRecord(record);
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading selected student for discount:', error);
          setSelectedStudentRecord(null);
        }
      } finally {
        if (!cancelled) {
          setLoadingStudentRecord(false);
        }
      }
    };

    loadSelectedStudent();

    return () => {
      cancelled = true;
    };
  }, [db, selectedStudentDocId]);

  useEffect(() => {
    if (selectedStudentCourseOptions.length === 0) {
      setSelectedCourseKey('');
      return;
    }

    const isCurrentKeyValid = selectedStudentCourseOptions.some((option) => option.key === selectedCourseKey);
    if (!isCurrentKeyValid) {
      setSelectedCourseKey(selectedStudentCourseOptions[0].key);
    }
  }, [selectedCourseKey, selectedStudentCourseOptions]);

  const handleSelectStudent = useCallback((student) => {
    setSelectedStudentDocId(student.studentDocId);
    setStudentSearchTerm(student.bio || student.userEmail || student.userId || '');
  }, []);

  const applyDiscount = useCallback(async () => {
    if (!selectedStudentDocId) {
      swal('Select a student', 'Choose the student who will receive the discount.', 'warning');
      return;
    }

    if (!selectedCourseOption) {
      swal('Select a course', 'Choose which course the discount should apply to.', 'warning');
      return;
    }

    const amount = Number(discountAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      swal('Invalid amount', 'Enter a valid discount amount.', 'warning');
      return;
    }

    if (!selectedStudentRecord) {
      swal('Hold on', 'We could not load the selected student profile yet.', 'error');
      return;
    }

    const reason = String(discountReason || '').trim();
    const now = Timestamp.now();
    const discountRef = doc(collection(db, 'StudentDiscounts'));
    const courseKey = selectedCourseOption.key;
    const discountRecord = {
      id: discountRef.id,
      studentDocId: selectedStudentDocId,
      studentId: selectedStudentRecord.userId || selectedStudentDocId,
      studentName: getStudentDisplayName(selectedStudentRecord),
      studentEmail: selectedStudentRecord.userEmail || '',
      phoneNumber: selectedStudentRecord.phoneNumber || '',
      studentStatus: selectedStudentRecord.userStatus || selectedStudentRecord.Status || '',
      courseName: selectedCourseOption.name,
      courseType: selectedCourseOption.type,
      courseKey,
      discountAmount: amount,
      discountReason: reason,
      createdAt: now,
      updatedAt: now,
      createdBy: auth.currentUser?.uid || '',
      createdByEmail: auth.currentUser?.email || '',
      source: 'finance_discount_page',
      active: true
    };

    setIsSavingDiscount(true);

    try {
      await setDoc(discountRef, discountRecord);

      await updateDoc(doc(db, 'users', selectedStudentDocId), {
        discountHistory: arrayUnion(discountRecord),
        feeDiscountUpdatedAt: now,
        updatedAt: now
      });

      await logAdminActivity({
        action: 'Apply Discount',
        details: `Applied KES ${amount.toLocaleString('en-KE')} discount to ${discountRecord.studentName}`,
        meta: {
          page: 'Student Discounts',
          studentId: discountRecord.studentId,
          studentName: discountRecord.studentName,
          courseName: discountRecord.courseName,
          discountAmount: amount
        }
      });

      swal('Discount applied', 'The discount has been saved successfully.', 'success');
      closeDiscountModal();
      await Promise.all([loadDiscounts(), loadStudentOptions()]);
    } catch (error) {
      console.error('Error applying student discount:', error);
      swal('Error', error?.message || 'Could not apply the discount. Please try again.', 'error');
    } finally {
      setIsSavingDiscount(false);
    }
  }, [
    auth,
    closeDiscountModal,
    db,
    discountAmount,
    discountReason,
    loadDiscounts,
    loadStudentOptions,
    selectedCourseOption,
    selectedStudentDocId,
    selectedStudentRecord
  ]);

  const viewStudent = useCallback((userId) => {
    if (!userId) return;
    navigate('/view-student', { state: { myData: userId } });
  }, [navigate]);

  return (
    <div className="clientsComp incomePage discountPage">
      <div className="incomeHero">
        <div className="incomeHeroCopy">
          <div className="incomeHeroTitleRow">
            <h5>Student Discounts</h5>
          </div>
          <p className="incomeHeaderMeta">
            <span>{filteredDiscounts.length} records</span>
            <span className="incomeInlineDot" />
            <span>{uniqueDiscountStudents} students</span>
            <span className="incomeInlineDot" />
            <span>Recorded fee reductions</span>
          </p>
        </div>
        <div className="incomeHeroActions">
          <Button variant="dark" onClick={openDiscountModal} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <FontAwesomeIcon icon={faPercent} />
            Discount
          </Button>
        </div>
      </div>

      <div className="incomeStatsGrid">
        <div className="incomeStatCard incomeStatPrimary">
          <span className="incomeStatLabel">Total Discounted</span>
          <h3>{formatAmount(totalDiscountAmount)}</h3>
          <p>Total amount discounted from the filtered records.</p>
        </div>
        <div className="incomeStatCard">
          <span className="incomeStatLabel">Discount Records</span>
          <h3>{filteredDiscounts.length}</h3>
          <p>Every discount recorded in the system.</p>
        </div>
        <div className="incomeStatCard">
          <span className="incomeStatLabel">Students Covered</span>
          <h3>{uniqueDiscountStudents}</h3>
          <p>Unique students with at least one discount.</p>
        </div>
        <div className="incomeStatCard">
          <span className="incomeStatLabel">Latest Discount</span>
          <h3>{filteredDiscounts[0] ? formatAmount(filteredDiscounts[0].discountAmount) : 'No records'}</h3>
          <p>{filteredDiscounts[0] ? `${filteredDiscounts[0].studentName || 'Student'} • ${formatDateTime(filteredDiscounts[0].createdAt)}` : 'Discount history will appear here.'}</p>
        </div>
      </div>

      <div className="searchContainer incomeToolbar">
        <div className="incomeToolbarMeta">
          <div className="input-group">
            <span className="input-group-text">
              <FontAwesomeIcon icon={faSearch} />
            </span>
            <Form.Control
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search student, course, amount, reason..."
            />
          </div>
        </div>
        <div className="incomeToolbarMeta">
          <span className="incomeToolbarText">Showing {filteredDiscounts.length} records</span>
        </div>
      </div>

      <div className="appsTableCard">
        <div className="appsTableHeader">
          <h6>Discount List</h6>
          <span>{filteredDiscounts.length} records</span>
        </div>
        <div className="appsTableWrap">
          <Table hover responsive className="appsTable">
            <thead>
              <tr>
                <th>Date</th>
                <th>Student</th>
                <th>Course</th>
                <th>Discount</th>
                <th>Reason</th>
                <th>Added By</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {!loading && filteredDiscounts.map((discount) => (
                <tr key={discount.id}>
                  <td>{formatDateTime(discount.createdAt)}</td>
                  <td>
                    <div className="appsStudentCell">
                      <div className="appDefProfPic">
                        <img src={defaulProfPic} alt="profile" />
                      </div>
                      <div>
                        <p className="appsStudentName">{discount.studentName || 'Unnamed student'}</p>
                        <span className="appsStudentMeta">
                          {discount.studentStatus || 'Status not set'} • {discount.studentEmail || discount.studentId || 'No email'}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="appsStudentName">
                      <p className="appsStudentName">{discount.courseName || 'Course'}</p>
                      <span className="appsStudentMeta">{discount.courseType === 'secondary' ? 'Secondary course' : 'Primary course'}</span>
                    </div>
                  </td>
                  <td>{formatAmount(discount.discountAmount)}</td>
                  <td>{discount.discountReason || '—'}</td>
                  <td>{discount.createdByEmail || 'Finance'}</td>
                  <td>
                    <Button className="appsViewBtn" onClick={() => viewStudent(discount.studentId || discount.studentDocId)}>
                      View
                    </Button>
                  </td>
                </tr>
              ))}
              {loading && (
                <tr>
                  <td colSpan="7" className="appsEmptyState">
                    Loading student discounts...
                  </td>
                </tr>
              )}
              {!loading && filteredDiscounts.length === 0 && (
                <tr>
                  <td colSpan="7" className="appsEmptyState">
                    No discounts recorded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      <Modal show={showDiscountModal} onHide={closeDiscountModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Discount</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">Search for any student status, pick the course, then save one discount at a time.</p>

          <div className="row g-3">
            <div className="col-12">
              <Form.Group>
                <Form.Label>Search student</Form.Label>
                <Form.Control
                  type="text"
                  value={studentSearchTerm}
                  onChange={(event) => setStudentSearchTerm(event.target.value)}
                  placeholder="Type name, course, phone, email, or status"
                  autoFocus
                />
              </Form.Group>
            </div>

            <div className="col-12">
              <div className="border rounded overflow-hidden bg-white" style={{ maxHeight: '280px', overflowY: 'auto' }}>
                {!studentSearchTerm.trim() && (
                  <div className="p-3 text-muted">
                    Start typing to find a student.
                  </div>
                )}

                {studentSearchTerm.trim() && loadingStudentOptions && (
                  <div className="d-flex align-items-center gap-2 p-3 text-muted">
                    <Spinner animation="border" size="sm" />
                    <span>Loading students...</span>
                  </div>
                )}

                {studentSearchTerm.trim() && !loadingStudentOptions && filteredStudentOptions.length === 0 && (
                  <div className="p-3 text-danger">
                    No students match your search.
                  </div>
                )}

                {filteredStudentOptions.map((student) => {
                  const isSelected = selectedStudentDocId === student.studentDocId;
                  return (
                    <button
                      key={student.studentDocId}
                      type="button"
                      onClick={() => handleSelectStudent(student)}
                      className="w-100 border-0 border-bottom text-start px-3 py-3"
                      style={{ outline: 'none', cursor: 'pointer', backgroundColor: isSelected ? '#e7f1ff' : '#fff' }}
                    >
                      <div className="d-flex justify-content-between align-items-start gap-3">
                        <div className="flex-grow-1">
                          <div className="fw-semibold text-dark">{getStudentDisplayName(student)}</div>
                          <div className="small text-muted">
                            <span className="me-2">{student.course || 'No course'}</span>
                            <span className="me-2">{student.userEmail || 'No email'}</span>
                            <span>{student.phoneNumber || 'No phone'}</span>
                          </div>
                        </div>
                        <div className="d-flex flex-column align-items-end gap-2">
                          <span className="badge bg-dark">{student.userStatus || 'Unknown'}</span>
                          <span className="small text-muted">{student.userId || student.studentDocId}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="col-12">
              <div className="p-3 border rounded bg-light">
                {loadingStudentRecord ? (
                  <div className="d-flex align-items-center gap-2">
                    <Spinner animation="border" size="sm" />
                    <span>Loading student profile...</span>
                  </div>
                ) : selectedStudentRecord ? (
                  <div className="d-flex flex-column gap-2">
                    <div className="d-flex flex-wrap gap-3">
                      <div>
                        <small className="text-muted d-block">Student</small>
                        <strong>{getStudentDisplayName(selectedStudentRecord)}</strong>
                      </div>
                      <div>
                        <small className="text-muted d-block">Status</small>
                        <strong>{selectedStudentRecord.userStatus || '—'}</strong>
                      </div>
                      <div>
                        <small className="text-muted d-block">Email</small>
                        <strong>{selectedStudentRecord.userEmail || '—'}</strong>
                      </div>
                    </div>

                    <Form.Group>
                      <Form.Label>Course</Form.Label>
                      <Form.Select
                        value={selectedCourseKey}
                        onChange={(event) => setSelectedCourseKey(event.target.value)}
                        disabled={selectedStudentCourseOptions.length === 0}
                      >
                        <option value="">
                          {selectedStudentCourseOptions.length > 0 ? 'Select a course' : 'No courses available'}
                        </option>
                        {selectedStudentCourseOptions.map((option) => (
                          <option key={option.key} value={option.key}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <div className="d-flex flex-wrap gap-3">
                      <div>
                        <small className="text-muted d-block">Phone</small>
                        <strong>{selectedStudentRecord.phoneNumber || '—'}</strong>
                      </div>
                      <div>
                        <small className="text-muted d-block">Current course</small>
                        <strong>{selectedStudentRecord.course || '—'}</strong>
                      </div>
                      <div>
                        <small className="text-muted d-block">Secondary courses</small>
                        <strong>{normalizeSecondaryCourses([
                          ...(Array.isArray(selectedStudentRecord.secondaryCourses) ? selectedStudentRecord.secondaryCourses : []),
                          selectedStudentRecord.pendingSecondaryCourse
                        ].filter(Boolean)).length}</strong>
                      </div>
                    </div>
                  </div>
                ) : (
                  <span className="text-muted">Select a student to continue.</span>
                )}
              </div>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Discount amount</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  step="1"
                  value={discountAmount}
                  onChange={(event) => setDiscountAmount(event.target.value)}
                  placeholder="Enter discount amount"
                />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Reason</Form.Label>
                <Form.Control
                  type="text"
                  value={discountReason}
                  onChange={(event) => setDiscountReason(event.target.value)}
                  placeholder="Optional reason for the discount"
                />
              </Form.Group>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDiscountModal} disabled={isSavingDiscount}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={applyDiscount}
            disabled={
              isSavingDiscount
              || loadingStudentRecord
              || !selectedStudentDocId
              || !selectedCourseOption
              || !discountAmount
            }
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
          >
            {isSavingDiscount ? (
              <>
                <Spinner animation="border" size="sm" />
                Saving...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPlus} />
                Implement Discount
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default StudentDiscountsComp;
